import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from "react";
import { BuzzRelayClient } from "./buzzRelay";
import {
  createIdentity,
  importIdentity,
  loadIdentity,
  type Identity,
} from "./identity";

interface BuzzContextValue {
  identity: Identity;
  client: BuzzRelayClient;
  resetIdentity: () => void;
  importSecret: (hex: string) => void;
  tick: number;
}

const BuzzContext = createContext<BuzzContextValue | null>(null);

export function BuzzProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity>(() => loadIdentity());
  const [tick, bump] = useReducer((n: number) => n + 1, 0);

  const client = useMemo(() => new BuzzRelayClient(identity), [identity]);

  useEffect(() => {
    const unsub = client.subscribe(() => bump());
    client.connect();
    return () => {
      unsub();
      client.dispose();
    };
  }, [client]);

  const value = useMemo<BuzzContextValue>(
    () => ({
      identity,
      client,
      tick,
      resetIdentity: () => setIdentity(createIdentity()),
      importSecret: (hex: string) => setIdentity(importIdentity(hex)),
    }),
    [identity, client, tick],
  );

  return <BuzzContext.Provider value={value}>{children}</BuzzContext.Provider>;
}

export function useBuzz(): BuzzContextValue {
  const ctx = useContext(BuzzContext);
  if (!ctx) throw new Error("useBuzz must be used within BuzzProvider");
  return ctx;
}
